import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { merge } from 'lodash-es';
import { SnackService } from "../../../services/snack.service";
import { ApiResponse } from "../../../classes/api-response";
import { Contract } from "../../../model/contract";
import { ContractsService } from "../../../services/contracts.service";
import { Company } from "../../../model/company";
import { Researcher } from "../../../model/researcher";
import { CompaniesService } from "../../../services/companies.service";
import { UsersService } from "../../../services/users.service";
import { AuthService } from "../../../services/auth.service";
import { Constants } from "../../../utils/constants";
import { Testbed } from "../../../model/testbed";
import { TestbedsService } from "../../../services/testbeds.service";
import { Box } from "../../../model/box";
import { BoxesService } from "../../../services/boxes-service";
import { PatchContract } from "../../../model/patch-contract";
import { MatDialog } from "@angular/material/dialog";
import { CreateTestbedDialogComponent } from "../../testbeds/create-testbed-dialog/create-testbed-dialog.component";
import { MatRadioChange } from "@angular/material/radio";
import { Customer } from "../../../model/customer";
import { Observable, of } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { DialogsService } from "../../../services/dialogs.service";
import { CreateStorageDialogComponent } from "../../storage/create-storage-dialog/create-storage-dialog.component";
import { Storage } from "../../../model/storage";

@Component({
  selector: 'aw-contract-form',
  templateUrl: './contract-form.component.html',
  styleUrls: ['./contract-form.component.scss']
})
export class ContractFormComponent implements OnInit {

  contractForm: UntypedFormGroup;
  @Output() saved = new EventEmitter<void>();
  @Output() canceled = new EventEmitter<void>();
  generalError?: ApiResponse;

  contractStatuses = Constants.contractStatuses;
  boxStatuses = Constants.boxStatuses;
  roles = Constants.roles;
  companies: Company[] = [];
  customers: Customer[] = [];
  researchers: Researcher[] = [];
  alwaysContainsAllTestbeds: Testbed[] = [];
  filteredTestbeds: Testbed[] = [];
  boxes: Box[] = [];
  loggedUser: any;
  loadingCompanies = false;
  loadingCustomers = false;
  loadingTestbeds = false;
  adminHasSelectedCompany = false;
  isEditing = false;
  saving = false;
  selectedCustomer: Customer | undefined = new Customer();
  selectedCompany: Company | undefined = new Company();
  selectedTestbed: Testbed | undefined = new Testbed();
  validatorsForUserRole: ValidatorFn[] = [];
  validatorForStatus: ValidatorFn[] = [];
  testbedReferentControls: AbstractControl[] = [];
  iAmCustomer = false;
  isSameReferent = false;
  testbedReferentFieldsHidden = false;
  minDate?: string;
  disclaimer?: string;
  isAlreadyBusy = false;
  entities = Constants.entities;
  contractTypes = Constants.contractTypes;
  general = Constants.general;

  customers$!: Observable<Customer[]>;

  contractType = this.contractTypes.directContract;

  constructor(
    private companiesService: CompaniesService,
    private usersService: UsersService,
    private authService: AuthService,
    private contractService: ContractsService,
    private testbedsService: TestbedsService,
    private boxesService: BoxesService,
    private snackService: SnackService,
    private changeDecRef: ChangeDetectorRef,
    public dialog: MatDialog,
    public dialogService: DialogsService
  ) {


    this.contractForm = new UntypedFormGroup(
      {
        contractType: new UntypedFormControl(
          null,
          []
        ),
        isSameReferent: new UntypedFormControl(
          null,
          []
        ),
        title: new UntypedFormControl(
          '',
          [
            Validators.required,
          ]
        ),
        description: new UntypedFormControl(
          '',
          [
            Validators.required,
          ]
        ),
        startDate: new UntypedFormControl(
          '',
          [
            Validators.required,
          ]
        ),
        endDate: new UntypedFormControl(
          '',
          [
            Validators.required,
          ]
        ),
        extendedEndDate: new UntypedFormControl(
          '',
          []
        ),
        note: new UntypedFormControl(
          null,
          []
        ),
        numberOfBoxes: new UntypedFormControl(
          '',
          [
            Validators.required,
            Validators.min(this.general.minimumNumberOfBoxesValue),
            Validators.max(this.general.maximumNumberOfBoxesValue),
          ]
        ),
        thirdPartyContract: new UntypedFormControl(
          null,
          []
        ),
        company: new UntypedFormControl(
          null,
          this.validatorsForUserRole
        ),
        testbed: new UntypedFormControl(
          null,
          []
        ),
        contractReferent: new UntypedFormControl(
          null,
          this.validatorsForUserRole
        ),
        testbedReferentFirstName: new UntypedFormControl(
          null,
          [
            Validators.required
          ]
        ),
        testbedReferentLastName: new UntypedFormControl(
          null,
          [
            Validators.required
          ]
        ),
        testbedReferentEmail: new UntypedFormControl(
          null,
          [
            Validators.required,
            Validators.pattern("[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?")
          ]
        ),
        testbedReferentPhoneNumber: new UntypedFormControl(
          null,
          [
            Validators.required,
            Validators.pattern('[- +()0-9]+')
          ]
        ),
      },
    );

    this.testbedReferentControls.push(
      this.contractForm.controls['testbedReferentFirstName'],
      this.contractForm.controls['testbedReferentLastName'],
      this.contractForm.controls['testbedReferentEmail'],
      this.contractForm.controls['testbedReferentPhoneNumber']
    );
  }

  _contract = new Contract();

  @Input() set contract(value: Contract) {
    this._contract = merge(new Contract(), value);
    this.selectedTestbed = value.testbed;
    this.selectedCompany = this.iAmCustomer ? this.loggedUser.company : value.company;
    this.selectedCustomer = this.iAmCustomer ? this.loggedUser : value.contractReferent;
    this.isEditing = !!this._contract.id;
    this.contractForm.patchValue(this._contract);

    if (value.thirdPartyContract)
      this.contractType = this.contractTypes.partnership;

    if (value.status !== undefined)
      this.clearTestbedReferentValidators();

  }

  ngOnInit(): void {

    this.customers$ =
      of(true).pipe(
        tap(() => this.loadingCustomers = true),
        switchMap(() => {
          return this.usersService.getAllCustomersByCompany(this.selectedCompany?.id);
        }),
        tap((x) => {
          this.customers = x;
          this.loadingCustomers = false;
        })
      );

    this.loggedUser = this.authService.loggedUser();
    this.iAmCustomer = this.loggedUser.role === this.roles.customer;

    if (this._contract.extendedEndDate)
      this.minDate = new Date(this._contract.extendedEndDate).toISOString();
    else if (this._contract.endDate)
      this.minDate = new Date(this._contract.endDate).toISOString();

    if (this._contract.status === this.contractStatuses.ongoing)
      this.validatorForStatus.push(Validators.required);


    if (this.iAmCustomer) {
      this.selectedCompany = this.loggedUser.company;
      this.selectedCustomer = this.loggedUser;
    }
    else {
      this.validatorsForUserRole.push(Validators.required);
    }

    // if i am creating as an admin
    if (!this.isEditing && !this.iAmCustomer) {
      this.loadingTestbeds = true;
      this.loadingCompanies = true;
      this.adminHasSelectedCompany = false;

      this.companiesService.getAllCompanies().subscribe(companies => {
        this.companies = companies;
        this.loadingCompanies = false;
      });

      this.testbedsService.getAllTestbeds().subscribe(testbeds => {
        this.alwaysContainsAllTestbeds = testbeds;
        testbeds = this.filterBusyTestbeds(testbeds);
        this.filteredTestbeds = testbeds;
        this.loadingTestbeds = false;
      });

    }

    if (this.iAmCustomer && (this._contract.status === this.contractStatuses.new || this._contract.status === this.contractStatuses.testbedAssociation || this._contract.status === undefined)) {
      this.loadingTestbeds = true;
      this.companies.push(this.loggedUser.company);

      this.testbedsService.getAllTestbedsByCompany(this.loggedUser.company.id).subscribe(testbeds => {
        this.alwaysContainsAllTestbeds = testbeds;
        testbeds = this.filterBusyTestbeds(testbeds);
        this.filteredTestbeds = testbeds;

        this.contractForm.controls["testbed"].setValue(this.filteredTestbeds.find(testbed => testbed.id === this._contract.testbed?.id));
        this.loadingTestbeds = false;
        this.changeDecRef.detectChanges();
      });
    }

    if (!this.iAmCustomer && this._contract.company && (this._contract.status === this.contractStatuses.new || this._contract.status === undefined)) {

      this.loadingTestbeds = true;

      this.testbedsService.getAllTestbedsByCompany(this._contract.company.id).subscribe(testbeds => {
        this.alwaysContainsAllTestbeds = testbeds;
        testbeds = this.filterBusyTestbeds(testbeds);
        this.filteredTestbeds = testbeds;
        this.contractForm.controls["testbed"].setValue(this.filteredTestbeds.find(testbed => testbed.id === this._contract.testbed?.id));
        this.loadingTestbeds = false;
        this.changeDecRef.detectChanges();
      });

    }

    this.changeDecRef.detectChanges();

  }

  getSixMonthsAfterStartDate(): Date {

    const startDateValue: Date = this.contractForm.controls['startDate'].value;

    if (startDateValue) {
      const startDateInMillis = new Date(startDateValue).getTime();
      const date = new Date(startDateInMillis + Constants.general.sixMonthsInMillis + Constants.general.oneDayInMillis);
      date.setHours(12);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
      return date;
    }

    return new Date(Date.now() + Constants.general.sixMonthsInMillis);

  }

  getSixMonthsBeforeEndDate(): Date | undefined {

    const endDateValue: Date = this.contractForm.controls['endDate'].value;

    if (endDateValue) {
      const endDateInMillis = new Date(endDateValue).getTime();
      const date = new Date(endDateInMillis - Constants.general.sixMonthsInMillis);
      date.setHours(12);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
      return date;
    }

    return undefined;

  }

  openTestbedForm(): void {
    this.dialog.open(CreateTestbedDialogComponent, { data: { creatingFromContract: true } })
      .afterClosed()
      .subscribe(
        testbed => {
          this.contractForm.controls["testbed"].setValue(testbed);
          this.selectedTestbed = testbed;
          if (this.selectedTestbed) {
            this.alwaysContainsAllTestbeds.push(this.selectedTestbed);
            this.filterTestbedsForCompany();
          }
          this.changeDecRef.detectChanges();
        });
  }

  saveContract(): void {
    this.saving = true;
    const newContract = merge(new Contract(), this._contract, this.contractForm.value);
    newContract.contractReferent = this.selectedCustomer;
    newContract.company = this.selectedCompany;
    if (this.testbedReferentFieldsHidden) {
      newContract.testbedReferentFirstName = this.selectedCustomer?.firstName;
      newContract.testbedReferentLastName = this.selectedCustomer?.lastName;
      newContract.testbedReferentEmail = this.selectedCustomer?.email;
      newContract.testbedReferentPhoneNumber = this.selectedCustomer?.phone;
    }

    this.generalError = undefined;

    if (this._contract.id) {

      switch (this._contract.status) {

        case this.contractStatuses.new:
          this.createContract(newContract);
          break;

        case this.contractStatuses.testbedAssociation:
          this.associateTestbed(newContract.company.id, newContract.id, newContract);
          break;

        case this.contractStatuses.boxesWaiting:

          this.associateBoxes(newContract.company.id, newContract.boxes, newContract, newContract.id);
          break;

        case this.contractStatuses.ongoing:
        case this.contractStatuses.extended:
          const patchContract = new PatchContract();
          patchContract.extendedEndDate = newContract.extendedEndDate;
          this.editContract(patchContract, newContract?.id);
          break;
      }

    } else
      this.createContract(newContract);
  }

  editContract(contract: Contract, contractId: number): void {

    this.contractService.updateContract(contract, contractId)
      .subscribe(
        () => {
          this.saving = false;
          this.contract = contract;
          this.snackService.success('Contract updated!');
          this.saved.emit();
        },
        error => {
          this.saving = false;
          this.snackService.error(error.message);
          this.generalError = error;
        });
  }

  associateTestbed(companyId: number, contractId: number, newContract: Contract | any): void {

    this.testbedsService.associateTestbed(companyId, newContract.testbed, contractId)
      .subscribe(() => {
        this.saving = false;
        this.contract = newContract;
        this.snackService.success('Contract updated!');
        this.saved.emit();
      },
        error => {
          this.saving = false;
          this.snackService.error(error.message);
          this.generalError = error;
        });
  }

  associateBoxes(companyId: number, boxes: Box[], newContract: Contract, contractId?: number): void {
    this.boxesService.associateBoxes(companyId, boxes, contractId).subscribe(
      () => {
        this.saving = false;
        this.contract = newContract;
        this.snackService.success('Contract updated!');
        this.saved.emit();
      },
      error => {
        this.saving = false;
        this.snackService.error(error.message);
        this.generalError = error;
      }
    );
  }

  filterTestbedsForCompany(): void {
    if (this.selectedCompany) {
      this.filteredTestbeds = [];
      this.filteredTestbeds = this.alwaysContainsAllTestbeds;
      this.filteredTestbeds = this.filteredTestbeds.filter(testbed => testbed.company?.id === this.selectedCompany?.id && !this.isTestbedBusy(testbed));
    } else {
      this.filteredTestbeds = this.alwaysContainsAllTestbeds;
    }
  }

  filterCustomersForCompany(): void {
    if (!this.iAmCustomer && this.selectedCompany) {
      this.customers$.subscribe(() => {
        this.adminHasSelectedCompany = true;
      });
    }
  }

  createContract(newContract: Contract): void {

    if (!this.iAmCustomer && newContract.testbed && !newContract.testbed.storage)
      this.dialogService.confirm("Contextual storage creation", `Do you want to create a storage for the testbed ${newContract.testbed.identifier}?`)
        .pipe(
          switchMap((value) => value ? this.dialog.open(CreateStorageDialogComponent, { data: { testbed: newContract.testbed, storage: new Storage() } }).afterClosed() : of(true)),
          switchMap(() => this.contractService.createContract(newContract))
        ).subscribe(
          () => {
            this.saving = false;
            this.contract = newContract;
            this.snackService.success('Contract created!');
            this.saved.emit();
          },
          error => {
            this.saving = false;
            this.snackService.error(error.message);
            this.generalError = error;
          });

    else
      this.contractService.createContract(newContract)
        .subscribe(
          () => {
            this.saving = false;
            this.contract = newContract;
            this.snackService.success('Contract created!');
            this.saved.emit();
          },
          error => {
            this.saving = false;
            this.snackService.error(error.message);
            this.generalError = error;
          });


  }

  filterBusyTestbeds(testbeds: Testbed[]): Testbed[] {
    testbeds.forEach(testbed => testbeds = this.isTestbedBusy(testbed) ? testbeds.filter(value => value.id !== testbed.id) : testbeds);
    return testbeds;
  }

  isTestbedBusy(testbed: Testbed): boolean {
    if ((testbed.contracts && testbed.contracts.length !== 0) && (testbed.id !== this._contract?.testbed?.id)) {
      return !!testbed.contracts.find(contract => contract.status === Constants.contractStatuses.new
        || contract.status === Constants.contractStatuses.approvalWaiting
        || contract.status === Constants.contractStatuses.testbedAssociation
        || contract.status === Constants.contractStatuses.boxesWaiting
        || contract.status === Constants.contractStatuses.ongoing
        || contract.status === Constants.contractStatuses.extended);
    }
    return false;
  }

  setContractType($event: MatRadioChange): void {
    this.contractType = $event.value;
    const control: AbstractControl = this.contractForm.controls['thirdPartyContract'];

    control.clearValidators();

    if (this.contractType == this.contractTypes.partnership) {
      const validatorForThirdParty: ValidatorFn[] = [Validators.required, Validators.min(this.general.minimumThirdPartyContractValue), Validators.max(this.general.maximumThirdPartyContractValue)];
      control.setValidators(validatorForThirdParty);
    }

    this.contractForm.controls['thirdPartyContract'].setValue(null);
    this.changeDecRef.detectChanges();

  }

  setTestbedReferentFieldsValidators(): void {
    this.testbedReferentFieldsHidden = !this.testbedReferentFieldsHidden;

    this.clearTestbedReferentValidators();

    if (!this.testbedReferentFieldsHidden) {
      this.contractForm.controls['testbedReferentFirstName'].setValidators([Validators.required]);
      this.contractForm.controls['testbedReferentLastName'].setValidators([Validators.required]);
      this.contractForm.controls['testbedReferentEmail'].setValidators([Validators.required, Validators.pattern("[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?")]);
      this.contractForm.controls['testbedReferentPhoneNumber'].setValidators([Validators.required, Validators.pattern('[- +()0-9]+')]);
    }

    this.contractForm.controls['testbedReferentFirstName'].setValue(null);
    this.contractForm.controls['testbedReferentLastName'].setValue(null);
    this.contractForm.controls['testbedReferentEmail'].setValue(null);
    this.contractForm.controls['testbedReferentPhoneNumber'].setValue(null);

    this.changeDecRef.detectChanges();
  }

  getFormValidationErrors(): void {
    Object.keys(this.contractForm.controls).forEach(key => {
      const controlErrors: any = this.contractForm.get(key)?.errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach(keyError => {
          console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
        });
      }
    });
  }

  private clearTestbedReferentValidators(): void {
    this.contractForm.controls['testbedReferentFirstName'].clearValidators();
    this.contractForm.controls['testbedReferentLastName'].clearValidators();
    this.contractForm.controls['testbedReferentEmail'].clearValidators();
    this.contractForm.controls['testbedReferentPhoneNumber'].clearValidators();
  }
}
